import type { ShipInfoVo } from "@zhihaoscm/entity";
import { ZhMap, type ZhMapRef } from "zhihao-ui-react";
import { useCallback, useEffect, useMemo, useRef } from "react";

/** 组件未传中心点时复用同一坐标引用，避免 ZhMap 将等值数组识别为初始化参数变化。 */
const DEFAULT_MAP_CENTER: [number, number] = [114.84, 30.52];

/** 业务页面依赖的地图能力；运行时对象始终由 zhihao-ui-react/ZhMap 提供。 */
export interface ZhihaoMapInstance {
    ship: { render(ships: ShipInfoVo[]): void; clear(): void; selected(ship?: ShipInfoVo): void };
    track?: { render(id: string, points: unknown[], color: string, options?: { showTime?: boolean; lineWidth?: number; showPopup?: boolean }): void | Promise<void>; close(id?: string): void; remove(): void; play?(id: string, speed: number): void; setCenter?(id?: string): void; getLength?(id: string): { length?: string | number } | undefined };
    carTrack?: { closeTrack(): void; location(vehicle?: unknown): void; setCenter(id?: string): void; renderTrack(id: string, points: unknown[], color: string): void; remove(): void; playTrack(id: string, speed: number): void };
    position?: { renderHtml(id: string, coordinate: [number, number], html: string, options?: { offset?: number[] }): void; removeHtml(id: string): void; clear?(): void };
    layer?: { setGreenTileVisible(show: boolean): void; setShowLayerType(type: "vector" | "satellite"): void };
    port?: Record<string, (...args: any[]) => any>;
    drawLine?: Record<string, (...args: any[]) => any>;
    drawPolygon?: Record<string, (...args: any[]) => any>;
    wharf?: Record<string, (...args: any[]) => any>;
    setCenter(longitude: number, latitude: number): void;
    setZoom(zoom: number): void;
    getZoom?(): number | undefined;
    getCenter?(): [number, number] | undefined;
    utils: { getCalculateExtent(): [number, number, number, number] | undefined; convertSixHundredThousandToLatLng?(longitude: number, latitude: number): [number, number] };
}

interface ZhihaoMapProps {
    label: string;
    layerType?: "vector" | "satellite";
    showGreenLayer?: boolean;
    ships?: ShipInfoVo[];
    center?: [number, number];
    markerText?: string;
    showCenterMarker?: boolean;
    selectedShipInfo?: ShipInfoVo;
    zoom?: number;
    track?: { id: string; points: unknown[]; color?: string };
    markers?: Array<{
        id: string;
        coordinate: [number, number];
        text: string;
        className?: string;
        variant?: "default" | "capture" | "ship";
        color?: string;
        interactive?: boolean;
    }>;
    onMarkerClick?: (id: string) => void;
    onViewportChange?: (extent: [number, number, number, number]) => void;
    onZoomChange?: (zoom: number) => void;
    onSelectShip?: (id: string) => void;
    onSelectPort?: (port: unknown) => void;
    onSelectWharf?: (wharf: unknown) => void;
    onLineDrawEnd?: (line: unknown) => void;
    onAreaDrawEnd?: (area: number[][]) => void;
    onReady?: (instance: ZhihaoMapInstance) => void;
}

/** 标记文字进入旧地图 HTML 覆盖层前先转义，避免地址文本被解释为标签。 */
const escapeHtml = (value: string) =>
    value.replace(
        /[&<>"]/g,
        character => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[character] ?? character
    );

/** 旧在途监管 CaptureMap 使用的 32×46 抓拍定位标记。 */
const createCaptureMarkerSvg = (color: string, sequence: string) =>
    `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="46" viewBox="0 0 32 46"><circle cx="16" cy="38" r="7.5" fill="#fff" stroke="#000"/><circle cx="16" cy="38" r="3" fill="#000"/><path fill="${escapeHtml(color)}" d="M32 15.6471C32 24.2887 18.2857 38 16 38C13.7143 38 0 24.2887 0 15.6471C0 7.0054 7.1634 0 16 0C24.8366 0 32 7.0054 32 15.6471Z"/><path fill="#fff" fill-rule="evenodd" d="M16 1.5C7.9602 1.5 1.5 7.8652 1.5 15.6471C1.5 17.4504 2.2295 19.6712 3.5048 22.0846C4.7654 24.4702 6.4869 26.9091 8.3005 29.1103C10.1129 31.3099 11.9871 33.2358 13.5291 34.5987C14.3028 35.2825 14.9682 35.8026 15.4824 36.142C15.7016 36.2866 15.8736 36.3852 16 36.4478C16.1264 36.3852 16.2984 36.2866 16.5176 36.142C17.0318 35.8026 17.6972 35.2825 18.4709 34.5987C20.0129 33.2358 21.8871 31.3099 23.6995 29.1103C25.5131 26.9091 27.2346 24.4702 28.4952 22.0846C29.7705 19.6712 30.5 17.4504 30.5 15.6471C30.5 7.8652 24.0398 1.5 16 1.5ZM16 38C18.2857 38 32 24.2887 32 15.6471C32 7.0054 24.8366 0 16 0C7.1634 0 0 7.0054 0 15.6471C0 24.2887 13.7143 38 16 38Z"/><text x="16" y="24" fill="#fff" font-family="PingFang SC, sans-serif" font-size="16" font-weight="500" text-anchor="middle">${escapeHtml(sequence)}</text></svg>`;

/** 旧在途监管远距离船位使用的绿色三角船标。 */
const createShipMarkerSvg = (color: string) =>
    `<svg width="41" height="24" viewBox="0 0 41 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M30.8843 12.6777L2.21343 21.7067C1.75559 21.8509 1.28947 21.509 1.28947 21.029L1.28947 2.97103C1.28947 2.49102 1.75558 2.14913 2.21342 2.29331L30.8843 11.3223C31.5471 11.531 31.5471 12.469 30.8843 12.6777Z" fill="${escapeHtml(color)}" stroke="${escapeHtml(color)}" stroke-width="0.578947"/><path d="M30 12L40 12" stroke="${escapeHtml(color)}" stroke-width="1.5"/></svg>`;

/** 将受控标记转换为旧地图 HTML Overlay，业务文本统一转义。 */
const createMarkerHtml = (marker: NonNullable<ZhihaoMapProps["markers"]>[number]) => {
    const markerId = marker.interactive === false ? "" : ` data-marker-id="${escapeHtml(marker.id)}"`;
    const className = escapeHtml(marker.className ?? "zhihao-map-marker");
    if (marker.variant === "capture") {
        return `<button type="button" class="${className}"${markerId}>${createCaptureMarkerSvg(marker.color ?? "#3370ff", marker.text)}</button>`;
    }
    if (marker.variant === "ship") {
        return `<div class="${className}">${createShipMarkerSvg(marker.color ?? "#04c900")}<span>${escapeHtml(marker.text)}</span></div>`;
    }
    return `<button type="button" class="${className}"${markerId}>${escapeHtml(marker.text)}</button>`;
};

/**
 * 业务受控数据同步到 zhihao-ui-react/ZhMap；地图渲染、船舶选择、轨迹和工具均由组件库负责。
 * 该适配层只保留现有页面所需的数据同步，后续页面迁移不得再接入 Vue 运行时。
 */
export function ZhihaoMap({
    label,
    layerType = "vector",
    showGreenLayer = false,
    ships = [],
    center,
    markerText,
    showCenterMarker = true,
    selectedShipInfo,
    zoom,
    track,
    markers = [],
    onMarkerClick,
    onViewportChange,
    onZoomChange,
    onSelectShip,
    onSelectPort,
    onSelectWharf,
    onLineDrawEnd,
    onAreaDrawEnd,
    onReady
}: ZhihaoMapProps) {
    const mapInstance = useRef<ZhihaoMapInstance | null>(null);
    const viewportCallback = useRef(onViewportChange);
    const zoomCallback = useRef(onZoomChange);
    const markerIdsRef = useRef<string[]>([]);
    const previousTrackIdRef = useRef<string | undefined>(undefined);
    const callbacksRef = useRef({ onSelectShip, onSelectPort, onSelectWharf, onLineDrawEnd, onAreaDrawEnd, onReady });
    const controlledStateRef = useRef({ ships, track, markers, center, showCenterMarker, markerText, showGreenLayer });
    viewportCallback.current = onViewportChange;
    zoomCallback.current = onZoomChange;
    callbacksRef.current = { onSelectShip, onSelectPort, onSelectWharf, onLineDrawEnd, onAreaDrawEnd, onReady };
    controlledStateRef.current = { ships, track, markers, center, showCenterMarker, markerText, showGreenLayer };

    /** ZhMap 会将中心点引用纳入初始化依赖，因此按坐标值缓存，不能在 JSX 中创建新数组。 */
    const mapCenter = useMemo<[number, number]>(
        () => center ? [center[0], center[1]] : DEFAULT_MAP_CENTER,
        [center?.[0], center?.[1]]
    );

    /**
     * React 组件库实例就绪后统一恢复地图状态；同一实例重复回填 ref 时必须保持幂等。
     * extent 响应会更新 ships 并触发重渲染，若每次 ref 回填都重新查询视口会形成无限请求闭环。
    */
    const setMapRef = useCallback((instance: ZhMapRef | null) => {
        if (!instance) return;
        const nextInstance = instance as unknown as ZhihaoMapInstance;
        if (mapInstance.current === nextInstance) return;
        mapInstance.current = nextInstance;
        const current = controlledStateRef.current;
        callbacksRef.current.onReady?.(nextInstance);
        // Vue 通过 runtime 切换绿点层；它不是底图初始化参数，实例重建后再恢复当前显隐状态。
        instance.layer.setGreenTileVisible(current.showGreenLayer);
        instance.ship.render(current.ships as never);
        if (current.track) void instance.track.render(current.track.id, current.track.points as never, current.track.color ?? "#ff0000");
        current.markers.forEach(marker => instance.position.renderHtml(marker.id, marker.coordinate, createMarkerHtml(marker)));
        if (current.center && current.showCenterMarker) {
            instance.position.renderHtml(
                "",
                current.center,
                `<div class="custom-content-marker"><img src="https://static.zhihaoscm.cn/map/poi-marker-default.png" alt="pointer-marker" /><div class="text">${escapeHtml(current.markerText ?? "")}</div></div>`,
                { offset: [-20, -60] }
            );
        }
        const extent = instance.utils.getCalculateExtent();
        if (extent) viewportCallback.current?.(extent as [number, number, number, number]);
    }, []);

    /**
     * zhihao-ui-react/ZhMap 会监听全部业务回调并据此重建 OpenLayers 实例。
     * 适配层必须提供稳定函数，再通过 ref 读取最新业务回调，避免数据回填时销毁底图。
     */
    const handleSelectShip = useCallback((id: string) => callbacksRef.current.onSelectShip?.(id), []);
    const handleSelectPort = useCallback((port: unknown) => callbacksRef.current.onSelectPort?.(port), []);
    const handleSelectWharf = useCallback((wharf: unknown) => callbacksRef.current.onSelectWharf?.(wharf), []);
    const handleLineDrawEnd = useCallback((lines: unknown) => callbacksRef.current.onLineDrawEnd?.(lines), []);
    const handleAreaDrawEnd = useCallback((area: number[][]) => callbacksRef.current.onAreaDrawEnd?.(area), []);
    const handleMapMoveEnd = useCallback((nextZoom: number) => {
        zoomCallback.current?.(nextZoom);
        if (nextZoom < 11) {
            mapInstance.current?.ship.clear();
            return;
        }
        const extent = mapInstance.current?.utils.getCalculateExtent();
        if (extent) viewportCallback.current?.(extent as [number, number, number, number]);
    }, []);

    useEffect(() => {
        mapInstance.current?.ship.render(ships);
    }, [ships]);

    useEffect(() => {
        if (track) {
            previousTrackIdRef.current = track.id;
            mapInstance.current?.track?.render(track.id, track.points, track.color ?? "#ff0000");
        } else {
            const runtimeTrack = mapInstance.current?.track;
            runtimeTrack?.close(previousTrackIdRef.current);
            runtimeTrack?.remove();
            previousTrackIdRef.current = undefined;
        }
    }, [track]);

    useEffect(() => {
        markerIdsRef.current.forEach(id => mapInstance.current?.position?.removeHtml(id));
        markers.forEach(marker =>
            mapInstance.current?.position?.renderHtml(marker.id, marker.coordinate, createMarkerHtml(marker))
        );
        markerIdsRef.current = markers.map(marker => marker.id);
    }, [markers]);

    useEffect(() => {
        mapInstance.current?.layer?.setGreenTileVisible(showGreenLayer);
    }, [showGreenLayer]);

    useEffect(() => {
        mapInstance.current?.layer?.setShowLayerType(layerType);
    }, [layerType]);

    useEffect(() => {
        if (center) mapInstance.current?.setCenter(center[0], center[1]);
    }, [center?.[0], center?.[1]]);

    useEffect(() => {
        if (zoom != null) mapInstance.current?.setZoom(zoom);
    }, [zoom]);

    useEffect(() => {
        const legacyMap = mapInstance.current;
        if (!legacyMap) return;
        if (!selectedShipInfo) {
            void legacyMap.ship.selected();
            return;
        }
        const longitude = Number(selectedShipInfo.lon);
        const latitude = Number(selectedShipInfo.lat);
        if (Number.isFinite(longitude) && Number.isFinite(latitude)) legacyMap.setCenter(longitude, latitude);
        // 等待船舶图层完成 render，复刻 Vue setShipCenter 后的红框选中时序。
        const timer = window.setTimeout(() => void legacyMap.ship.selected(selectedShipInfo), 100);
        return () => window.clearTimeout(timer);
    }, [selectedShipInfo]);

    return (
        <div
            className="zhihao-map-runtime"
            data-map-runtime={label}
            aria-label={`${label}地图`}
            onClick={event => {
                const marker = (event.target as HTMLElement).closest<HTMLElement>("[data-marker-id]");
                if (marker?.dataset.markerId) onMarkerClick?.(marker.dataset.markerId);
            }}
        >
            <ZhMap
                ref={setMapRef}
                center={mapCenter}
                zoom={zoom ?? (layerType === "satellite" ? 13 : 5)}
                layerType={layerType}
                showScale
                scaleLineUnit={layerType === "satellite" ? "nautical" : "metric"}
                copyrightImgUrl={
                    layerType === "satellite"
                        ? "https://static.zhihaoscm.cn/map/tdt-copyright-h-w.svg"
                        : "https://static.zhihaoscm.cn/map/tdt-copyright-h.svg"
                }
                selectShip={handleSelectShip}
                selectPort={handleSelectPort}
                selectWharf={handleSelectWharf}
                lineDrawEnd={handleLineDrawEnd}
                areaDrawEnd={handleAreaDrawEnd}
                mapMoveEnd={handleMapMoveEnd}
            />
        </div>
    );
}
